/**
 * Wrapper shim around `radix-ui` Popover.
 *
 * Exists so consuming code imports a stable symbol from `lib/ui/popover` —
 * insulating the FormCacheSelector from a future swap to a different popover
 * primitive (e.g. when Siesa UI Kit eventually ships one). It also gives unit
 * tests a single, easy `vi.mock` seam.
 *
 * `PopoverContent` wraps `PopoverPrimitive.Content` in an explicit
 * `PopoverPrimitive.Portal` so the panel is portalled to `document.body`.
 * Radix v1 does NOT do this automatically — without the Portal the panel
 * renders in-flow and stacking/z-index break.
 */

import * as React from 'react';
import { Popover as PopoverPrimitive } from 'radix-ui';

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverPortal = PopoverPrimitive.Portal;
export const PopoverClose = PopoverPrimitive.Close;

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className = '', align = 'start', sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={`z-50 min-w-[12rem] overflow-hidden rounded-md border border-gray-200 bg-white text-gray-900 shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out ${className}`}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = 'PopoverContent';
